import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { useServerStore } from '../../store/serverStore';
import { useAuthStore } from '../../store/authStore';
import {
  X, Settings, Link2, Users, Shield, Ban, Gamepad2, Trash2,
  AlertCircle,
} from 'lucide-react';
import OverviewTab from './settings/OverviewTab';
import InvitationsTab from './settings/InvitationsTab';
import MembersTab from './settings/MembersTab';
import RolesTab from './settings/RolesTab';
import BansTab from './settings/BansTab';
import GameServerTab from './settings/GameServerTab';
import DeleteServerModal from './settings/DeleteServerModal';

interface ServerSettingsModalProps {
  serverId: string;
  onClose: () => void;
}

/* ─── Tab / category definitions ─── */
interface Tab { id: string; label: string; icon: React.ElementType }
interface Category { label: string; tabs: Tab[] }

const CATEGORIES: Category[] = [
  {
    label: 'General',
    tabs: [
      { id: 'overview', label: 'Overview', icon: Settings },
      { id: 'invites', label: 'Invitations', icon: Link2 },
    ],
  },
  {
    label: 'Community',
    tabs: [
      { id: 'members', label: 'Members', icon: Users },
      { id: 'roles', label: 'Roles', icon: Shield },
      { id: 'bans', label: 'Bans', icon: Ban },
    ],
  },
  {
    label: 'Gaming',
    tabs: [
      { id: 'game_server', label: 'Game Server', icon: Gamepad2 },
    ],
  },
];

const ServerSettingsModal: React.FC<ServerSettingsModalProps> = ({ serverId, onClose }) => {
  const { currentServer } = useServerStore();
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const isOwner = currentServer?.owner_id === user?.id;

  /* ESC to close */
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleDeleteServer = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('Not authorized');
      await axios.delete(
        `${import.meta.env.VITE_API_URL}/servers/${serverId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onClose();
      window.location.reload();
    } catch {
      // Error handling is in the modal
    }
  };

  /* Access denied screen */
  if (!currentServer || !isOwner) {
    return ReactDOM.createPortal(
      <div
        className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-in"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div className="bg-bg-primary rounded-2xl w-full max-w-md border border-border-color shadow-2xl shadow-black/50 p-8 text-center animate-slide-up">
          <AlertCircle className="h-12 w-12 text-warning mx-auto mb-4" />
          <p className="text-text-primary text-lg font-bold mb-2">Access Denied</p>
          <p className="text-text-secondary text-sm">Only the server owner can access server settings.</p>
          <button
            onClick={onClose}
            className="mt-6 px-5 py-2 rounded-lg bg-accent-color text-black text-xs font-bold hover:bg-accent-hover transition-all"
          >
            Close
          </button>
        </div>
      </div>
    , document.body);
  }

  /* ════════ Tab content renderer ════════ */
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab serverId={serverId} />;
      case 'invites':
        return <InvitationsTab serverId={serverId} serverName={currentServer.name} />;
      case 'members':
        return <MembersTab serverId={serverId} ownerId={currentServer.owner_id} currentUserId={user!.id} />;
      case 'roles':
        return <RolesTab serverId={serverId} ownerId={currentServer.owner_id} currentUserId={user!.id} />;
      case 'bans':
        return <BansTab serverId={serverId} />;
      case 'game_server':
        return <GameServerTab serverId={serverId} onClose={onClose} />;
      default:
        return null;
    }
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-in"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative bg-bg-primary rounded-2xl w-[95vw] max-w-[80rem] h-[92vh] flex overflow-hidden border border-border-color shadow-2xl shadow-black/50 animate-slide-up">
        {/* ── Sidebar ── */}
        <aside className="w-56 flex-shrink-0 bg-bg-secondary/60 border-r border-border-color flex flex-col">
          <div className="flex-1 overflow-y-auto p-3 gaming-scrollbar">
            <h2 className="px-3 pt-2 pb-4 text-[13px] font-bold text-text-primary">
              Server Settings
            </h2>

            {CATEGORIES.map((cat) => (
              <div key={cat.label} className="mb-4">
                <p className="px-3 mb-1 text-[10px] font-bold text-text-muted uppercase tracking-[0.12em]">
                  {cat.label}
                </p>
                <div className="space-y-0.5">
                  {cat.tabs.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-all ${
                          isActive
                            ? 'bg-accent-color/10 text-accent-color'
                            : 'text-text-secondary hover:text-text-primary hover:bg-highlight/50'
                        }`}
                      >
                        <Icon className="h-4 w-4 flex-shrink-0" />
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Delete server */}
          <div className="p-3 border-t border-border-color">
            <button
              onClick={() => setShowDeleteModal(true)}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium text-error hover:bg-error/10 transition-all"
            >
              <Trash2 className="h-4 w-4" />
              Delete Server
            </button>
          </div>
        </aside>

        {/* ── Content ── */}
        <main className="flex-1 overflow-y-auto p-8 gaming-scrollbar">
          {renderTabContent()}
        </main>

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-lg bg-bg-tertiary/80 border border-border-color flex items-center justify-center text-text-muted hover:text-text-primary hover:bg-highlight transition-all"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <DeleteServerModal
          serverName={currentServer.name}
          onConfirm={handleDeleteServer}
          onClose={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  , document.body);
};

export default ServerSettingsModal;
